// Copyright (c) Ping. All rights reserved.

import { log } from '@core/log/log';
import { del, post } from '@core/network/api';
import { preferencesStore } from '@core/preferences/PreferencesStore';

import { readVapidPublicKey, urlBase64ToUint8Array } from './vapid';

const SW_URL = '/push-sw.js';
const SW_SCOPE = '/';

function pushSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

async function ensureServiceWorker(): Promise<ServiceWorkerRegistration | undefined> {
  const existing = await navigator.serviceWorker.getRegistration(SW_SCOPE);
  if (existing?.active) {
    return existing;
  }
  return navigator.serviceWorker.register(SW_URL, { scope: SW_SCOPE });
}

async function requestNotificationPermission(): Promise<NotificationPermission> {
  if (Notification.permission === 'granted' || Notification.permission === 'denied') {
    return Notification.permission;
  }
  return Notification.requestPermission();
}

/**
 * Registers (or refreshes) a Web Push subscription and POSTs it to
 * `/v1/push/tokens`. Best-effort; safe to call after every bootstrap.
 */
export async function registerWebPush(): Promise<void> {
  if (!pushSupported()) {
    log.debug({ event: 'push.register.skip', reason: 'unsupported' });
    return;
  }
  if (!preferencesStore.getState().notificationsEnabled) {
    log.debug({ event: 'push.register.skip', reason: 'notifications_disabled' });
    return;
  }

  const vapidPublic = readVapidPublicKey();
  if (vapidPublic === undefined) {
    log.debug({ event: 'push.register.skip', reason: 'vapid_unconfigured' });
    return;
  }

  const permission = await requestNotificationPermission();
  if (permission !== 'granted') {
    log.info({ event: 'push.register.skip', reason: 'permission_denied' });
    return;
  }

  try {
    const registration = await ensureServiceWorker();
    if (registration === undefined) {
      log.warn({ event: 'push.register.failed', reason: 'no_service_worker' });
      return;
    }
    await navigator.serviceWorker.ready;

    const appServerKey = urlBase64ToUint8Array(vapidPublic);
    let subscription = await registration.pushManager.getSubscription();
    if (subscription === null) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: appServerKey,
      });
    }

    const token = JSON.stringify(subscription.toJSON());
    await post('/v1/push/tokens', {
      platform: 'webpush',
      token,
    });

    log.info({
      event: 'push.register.ok',
      endpointPrefix: subscription.endpoint.slice(0, 32),
    });
  } catch (err) {
    log.warn({ event: 'push.register.failed', err: (err as Error).message });
  }
}

/** Best-effort server + browser unsubscribe (call before sign-out while JWT is valid). */
export async function unregisterWebPush(): Promise<void> {
  if (!pushSupported()) {
    return;
  }
  try {
    const registration = await navigator.serviceWorker.getRegistration(SW_SCOPE);
    const subscription = await registration?.pushManager.getSubscription();
    if (subscription != null) {
      const token = JSON.stringify(subscription.toJSON());
      try {
        await del('/v1/push/tokens', { platform: 'webpush', token });
        log.info({ event: 'push.unregister.ok' });
      } catch (err) {
        log.warn({ event: 'push.unregister.api_failed', err: (err as Error).message });
      }
      await subscription.unsubscribe();
    }
  } catch (err) {
    log.warn({ event: 'push.unregister.failed', err: (err as Error).message });
  }
}
